import {Stack, Text} from '@mantine/core';
import React, {useCallback, useMemo, useState} from 'react';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import type {TagSchema} from 'constants/RegistryConstants';
import {RegistryLabelingStrategy} from 'graphql-client/__generated/graphqlGlobalTypes';
import RegistrationConfigPane from 'registry/create-bioentity/registration-config/RegistrationConfigPane';
import type {DraftRegistrationConfig} from 'registry/create-bioentity/registration-config/RegistrationConfigTypes';
import TagSchemaSelector from 'schemas/shared/TagSchemaSelector';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';

import type {ModalAction, ModalState} from './moleculeImportReducer';
import useMoleculeSchemasPairing from './useMoleculeSchemasPairing';

interface ConfigurePaneProps {
  state: ModalState;
  dispatch: React.Dispatch<ModalAction>;
}

export default function ConfigurePane({state, dispatch}: ConfigurePaneProps): JSX.Element {
  const {pairingMap} = useMoleculeSchemasPairing(null);
  const [warningDismissed, setWarningDismissed] = useState(false);
  const [schemaLabelingStrategies, setSchemaLabelingStrategies] = useState<
    Array<RegistryLabelingStrategy> | undefined
  >(undefined);

  const handleSchemaChange = useCallback(
    (tagSchema: TagSchema | null) => {
      setWarningDismissed(false);
      if (!tagSchema) {
        dispatch({type: 'SET_SCHEMA', payload: {schemaId: null, compoundSchemaId: null}});
        dispatch({type: 'SET_REGISTRY_ID', payload: {registryId: null}});
        setSchemaLabelingStrategies(undefined);
        return;
      }
      const pairing = pairingMap[tagSchema.id];
      dispatch({
        type: 'SET_SCHEMA',
        payload: {
          schemaId: tagSchema.id,
          compoundSchemaId: pairing?.compoundSchemaId ?? null,
        },
      });
      dispatch({type: 'SET_REGISTRY_ID', payload: {registryId: tagSchema.registryId}});
      setSchemaLabelingStrategies(tagSchema.labelingStrategies);
      if (
        state.registrationConfig?.namingStrategy &&
        !tagSchema.labelingStrategies.includes(state.registrationConfig.namingStrategy)
      ) {
        dispatch({type: 'SET_NAMING_STRATEGY', payload: {namingStrategy: tagSchema.labelingStrategies[0]}});
      }
    },
    [dispatch, pairingMap, state.registrationConfig?.namingStrategy]
  );

  const registrationConfig: DraftRegistrationConfig = useMemo(
    () => ({
      registryId: state.registrationConfig?.registryId ?? null,
      namingStrategy: state.registrationConfig?.namingStrategy ?? null,
      folderId: state.registrationConfig?.folderId ?? state.initialFolderId ?? null,
    }),
    [state.registrationConfig, state.initialFolderId]
  );

  const handleRegistrationConfigChange = useCallback(
    (config: DraftRegistrationConfig) => {
      if (config.registryId !== registrationConfig.registryId) {
        dispatch({type: 'SET_REGISTRY_ID', payload: {registryId: config.registryId}});
      }
      if (config.namingStrategy !== registrationConfig.namingStrategy && config.namingStrategy != null) {
        dispatch({type: 'SET_NAMING_STRATEGY', payload: {namingStrategy: config.namingStrategy}});
      }
      if (config.folderId !== registrationConfig.folderId) {
        dispatch({type: 'SET_FOLDER_ID', payload: {folderId: config.folderId}});
      }
    },
    [dispatch, registrationConfig]
  );

  const schemaBanner = useMemo(() => {
    if (!state.schemaId) {
      return null;
    }
    const pairing = pairingMap[state.schemaId];
    if (!pairing) {
      if (warningDismissed) {
        return null;
      }
      return {
        type: InlineBannerType.WARNING,
        text: 'The schema you selected does not support the import of salt or solvate molecules.',
        supportingInfo:
          'Salt or solvate molecules in your upload will fail to be imported with the schema you selected. Select a different schema, or continue to upload.',
        dismissible: true,
      };
    }
    const linkedSchemaName =
      state.schemaId === pairing.formSchemaId ? pairing.compoundSchemaName : pairing.formSchemaName;
    return {
      type: InlineBannerType.INFO,
      text: 'This schema supports both standard molecules and salted or solvate forms.',
      supportingInfo: `All molecule types in your upload will be created using this schema or its linked ${linkedSchemaName}.`,
      dismissible: false,
    };
  }, [state.schemaId, pairingMap, warningDismissed]);

  return (
    <Stack spacing="md">
      <div>
        <Text weight={600} size="sm">
          Select a Schema
          <Text component="span" color="red">
            {' '}
            *
          </Text>
        </Text>
        <Text size="sm" color="dimmed">
          Molecule entities will be created based on the schema you select.
        </Text>
        <TagSchemaSelector
          oligoAwareFolderItemTypes={[ModelTypeConstants.MOLECULE]}
          selectedTagSchemaId={state.schemaId}
          onChange={handleSchemaChange}
        />
      </div>

      {schemaBanner && (
        <InlineBanner
          bannerType={schemaBanner.type}
          text={schemaBanner.text}
          supportingInfo={schemaBanner.supportingInfo}
          onDismiss={
            schemaBanner.dismissible
              ? () => {
                  setWarningDismissed(true);
                }
              : undefined
          }
        />
      )}

      <RegistrationConfigPane
        registrationConfig={registrationConfig}
        onRegistrationConfigChange={handleRegistrationConfigChange}
        registryReadOnly={true}
        allowedLabelingStrategies={schemaLabelingStrategies}
      />
    </Stack>
  );
}
